import { useCallback, useEffect, useMemo, useRef } from 'react';

import { useSearchLeadsMutation } from '@app/api/Leads';
import { useUserPrint } from '@app/hooks';
import { useLeadsRegistryFilters } from '@app/pages/Leads/LeadsRegistryPage/hooks/useLeadsRegistryFilters';
import { useLeadsRegistrySettings } from '@app/pages/Leads/LeadsRegistryPage/hooks/useLeadsRegistrySettings';
import { buildLeadsRegistrySearchFilter } from '@app/pages/Leads/LeadsRegistryPage/model/adapters/buildLeadsRegistrySearchFilter';
import { buildLeadsRegistrySearchSignature } from '@app/pages/Leads/LeadsRegistryPage/model/adapters/buildLeadsRegistrySearchSignature';
import {
  mergeLeads,
  normalizeLeadsPortionSize,
} from '@app/pages/Leads/LeadsRegistryPage/utils';
import { useLatestRegistryResultBridge } from '@app/pages/Leads/model';
import { useNotification } from '@app/providers/NotificationProvider/NotificationContext';
import { bigNumberToString } from '@app/utils/bigNumberToString';

interface UseLeadCardRegistryListRestoreParams {
  isEnabled: boolean;
}

interface RunLeadCardRegistrySearchParams {
  maxItems: number;
  isNextPage: boolean;
}

export const useLeadCardRegistryListRestore = ({
  isEnabled,
}: UseLeadCardRegistryListRestoreParams) => {
  const { params } = useUserPrint();
  const activeBranchId = params.ActiveBranchId;
  const { showNotification } = useNotification();
  const { filters } = useLeadsRegistryFilters();
  const { loadBatchSize } = useLeadsRegistrySettings();
  const [searchLeads] = useSearchLeadsMutation();
  const {
    acceptLatestResult,
    hasAcceptedResult,
    leads,
    requestedMaxSize,
    searchSignature: acceptedSearchSignature,
    setLoading,
    setNextPageLoading,
  } = useLatestRegistryResultBridge();

  const requestIdRef = useRef(0);
  const prevSearchSignatureRef = useRef<string | null>(null);
  const prevRequestedMaxSizeRef = useRef<number | null>(null);
  const prevHadValidAcceptedResultRef = useRef(false);

  const searchFilter = useMemo(
    () => buildLeadsRegistrySearchFilter(filters),
    [filters]
  );

  const searchSignature = useMemo(
    () => buildLeadsRegistrySearchSignature(searchFilter),
    [searchFilter]
  );

  const runSearch = useCallback(
    async ({ maxItems, isNextPage }: RunLeadCardRegistrySearchParams) => {
      if (!activeBranchId) return;

      const requestId = ++requestIdRef.current;

      if (isNextPage) {
        setNextPageLoading(true);
      } else {
        setLoading(true);
      }

      try {
        const normalizedMaxItems = normalizeLeadsPortionSize(maxItems);
        const response = await searchLeads({
          ActiveBranchId: activeBranchId,
          data: {
            ...searchFilter,
            MaxItems: normalizedMaxItems,
          },
        }).unwrap();

        if (requestId !== requestIdRef.current) return;

        acceptLatestResult({
          leads: isNextPage
            ? mergeLeads(leads, response.Items ?? [])
            : response.Items ?? [],
          totalCount: response.CountTotal ?? null,
          searchId: bigNumberToString(response.SearchId) || null,
          searchSignature,
          maxSize: normalizeLeadsPortionSize(loadBatchSize),
          requestedMaxSize: normalizedMaxItems,
        });
      } catch (error) {
        if (requestId === requestIdRef.current) {
          setLoading(false);
          setNextPageLoading(false);
          showNotification(error, 'warning');
        }
      }
    },
    [
      acceptLatestResult,
      activeBranchId,
      leads,
      loadBatchSize,
      searchFilter,
      searchLeads,
      searchSignature,
      setLoading,
      setNextPageLoading,
      showNotification,
    ]
  );

  const runSearchRef = useRef(runSearch);
  runSearchRef.current = runSearch;

  useEffect(() => {
    if (!isEnabled || !activeBranchId) return;

    const normalizedLoadBatchSize = normalizeLeadsPortionSize(loadBatchSize);
    const prevSearchSignature = prevSearchSignatureRef.current;
    const prevRequestedMaxSize = prevRequestedMaxSizeRef.current;
    const prevHadValidAcceptedResult = prevHadValidAcceptedResultRef.current;
    const isFirstRun = prevSearchSignature === null;
    const isFiltersChanged =
      !isFirstRun && prevSearchSignature !== searchSignature;
    const hasValidAcceptedResult =
      hasAcceptedResult && acceptedSearchSignature === searchSignature;
    const nextMaxItems = hasValidAcceptedResult
      ? requestedMaxSize
      : normalizedLoadBatchSize;

    prevSearchSignatureRef.current = searchSignature;
    prevRequestedMaxSizeRef.current = nextMaxItems;
    prevHadValidAcceptedResultRef.current = hasValidAcceptedResult;

    if (isFirstRun && hasValidAcceptedResult) return;

    const isNextPage =
      prevHadValidAcceptedResult &&
      !isFirstRun &&
      !isFiltersChanged &&
      prevRequestedMaxSize !== null &&
      nextMaxItems > prevRequestedMaxSize;

    if (hasValidAcceptedResult && !isNextPage && !isFiltersChanged) return;

    void runSearchRef.current({
      maxItems: nextMaxItems,
      isNextPage,
    });
  }, [
    acceptedSearchSignature,
    activeBranchId,
    hasAcceptedResult,
    isEnabled,
    loadBatchSize,
    requestedMaxSize,
    searchSignature,
  ]);
};
